import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import s from './ModelTelemetryLink.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { initialModelTelemetryLinkState } from '../../reducers/initialState';
import ModelTelemetryLinkListTable from './ModelTelemetryLinkListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import { Link } from 'react-router-dom';
import { getTelemetryLinks, postTelemetryLink, setCurrentPage, setTelemetryLinkChange, setShowScreenStatusThunk } from '../../actions/telemetryLinks';
import { getModelTelemetryLinks, getUserProfileWidget, getUserOrgModelCategoryType } from '../../selectors/index';
import Cookies from 'js-cookie';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { compareValues, logEntry } from '../../utils';
import { TELEMETRY_LINKS_MANAGEMENT, UPDATE_TELEMETRY_LINK } from '../../constants/logs';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import Banner from '../Banner/Banner';
import { v4 as uuid } from 'uuid';
import { useParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import { getModelCategoryPermission } from '../../utils';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Pagination from '../Pagination/Pagination';
import telemetryLinks from '../../reducers/telemetryLinks';

const ModelTelemetryLinkList = ({ intl }) => {
  const dispatch = useDispatch();
  let { ModelCategoryId, ModelId } = useParams();

  const telemetryLinkManager = useSelector(state => getModelTelemetryLinks(state));
  const userProfileWidgets = useSelector(state => getUserProfileWidget(state));
  const userModelCategories = useSelector(state => getUserOrgModelCategoryType(state));

  const [currentEditId, setCurrentEditId] = useState(-1);
  const [isFilter, setIsFilter] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [active, setActive] = useState(false);
  const [telemetryLinkId, setTelemetryLinkId] = useState(0);

  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  useEffect(() => {
    dispatch(getTelemetryLinks(ModelId, 'MODEL_TELEMETRY_LINKS'));
  }, [getTelemetryLinks, ModelId]);

  let initialLimit = 6;
  let initialRecords = telemetryLinkManager && telemetryLinkManager.TelemetryLinks.slice(0, initialLimit);

  let messageId = (telemetryLinkManager && telemetryLinkManager.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });
  useEffect(() => {
    if (messageId === 'refresh') dispatch(getTelemetryLinks(ModelId, 'MODEL_TELEMETRY_LINKS'));
  }, [getTelemetryLinks, messageId]);


  const onPageChangeHandler = currentPage => {
    dispatch(setCurrentPage(currentPage));
  };

  const disableModeHandler = status => {
    dispatch(setShowScreenStatusThunk(status));
  };

  const setAddHandler = () => {
    dispatch(setTelemetryLinkChange({
      selectedTelemetryLink: {
        ...initialModelTelemetryLinkState.TelemetryLink.selectedTelemetryLink,
        ModelId: ModelId,
        ModelCategoryId: ModelCategoryId
      }
    }));
    setCurrentEditId(0);
    dispatch(setShowScreenStatusThunk(true));
  };

  const setEditHandler = modelThresholdId => {
    setCurrentEditId(modelThresholdId);
    if (modelThresholdId === -1) {
      dispatch(setTelemetryLinkChange({
        selectedTelemetryLink: {
          ...initialModelTelemetryLinkState.TelemetryLink.selectedTelemetryLink,
          ModelId: ModelId,
          ModelCategoryId: ModelCategoryId
        }
      }));
      dispatch(setShowScreenStatusThunk(false));
    } else {
      dispatch(setShowScreenStatusThunk(true));
    }
  };


  const deleteTelemetry = telemetryLinkId => {
    let saveData = {
      TelemetryLinkId: telemetryLinkId,
      ModelId: ModelId,
      ModelCategoryId: ModelCategoryId,
      PerformDelete: true,
      UpdatedBy: Cookies.get(`userid-${envName}`) || 0
    };

    let actionName = UPDATE_TELEMETRY_LINK;
    let log = logEntry(TELEMETRY_LINKS_MANAGEMENT, actionName, saveData);
    dispatch(postTelemetryLink(saveData, log, 'MODEL_TELEMETRY_LINKS_REMOVE'));
  };

  const searchHandler = value => {
    if (value === '') {
      setIsFilter(false);
    } else {
      setIsFilter(true);
    }
    setSearchText(value);


  };

  const filterData = () => {
    let filteredList = telemetryLinkManager.TelemetryLinks;
    if (isFilter) {
      filteredList = telemetryLinkManager.TelemetryLinks.filter(function (item) {
        return item.TargetComponentPositionName.toLowerCase().includes(searchText.toLowerCase());
      });
    }

    return filteredList;
  };



  const handleConfirm = () => {
    deleteTelemetry(telemetryLinkId);
    handleClose();
  };

  const handleClose = () => setActive(false);

  const handleRemove = telemetryLinkId => {
    setActive(true);
    setTelemetryLinkId(telemetryLinkId);
  };

  const dialogTitle = intl.formatMessage({
    id: 'model.telemetryLinks.areYouSureYouWantToRemoveTheTelemetryLink',
    defaultMessage: 'Are you sure you want to remove the Telemetry link?'
  });

  const IsChangeAllowed = IsWidgetAccessible(userProfileWidgets, 'MODEL_TELEMETRY_LINKS_EDIT');
  const IsRemoveAllowed = IsWidgetAccessible(userProfileWidgets, 'MODEL_TELEMETRY_LINKS_REMOVE');


  let isOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.manageModelComponentPositionTelemetryLinks" defaultMessage="Manage Telemetry Links" /> } // Last item has no href (current page)
  ];

  const dataObj = Object.values(filterData());

  return (
    <div className={s.telemetryLink}>
      {telemetryLinkManager && telemetryLinkManager.isLoading && <LoadingSpinner />}
      <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
      <Banner
        key={uuid()}
        failureText={messageText}
        showBanner={telemetryLinkManager.showBanner}
        status={telemetryLinkManager.isOpSuccessful}
        successText={messageText}
      />
      <ModelConfigurationContentTemplate selectedPage="manageModelComponentPositionTelemetryLinks" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="model.manageModelThresholds" defaultMessage="Manage Model Thresholds" />
              <span className={s.circle}>
                {telemetryLinkManager.TelemetryLinks.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <Row className={s.topRow}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                // initialText={filter}
                placeHolderTextId="model.telemetryLinks.position"
                data-unittest="searchTelemetryLink"
              />
            </Col>
            <Col sm={6} lg={8}>
              {isOwner ? (
                <Button
                  className={s.addbutton}
                  style={WidgetVisibility(userProfileWidgets, 'MODEL_TELEMETRY_LINKS_ADD')}
                  onClick={setAddHandler}
                  disabled={telemetryLinkManager.showAddEditScreen}
                  data-unittest="addTelemetryLink"
                >
                  + <FormattedMessage id="common.addNew" defaultMessage="ADD NEW" />
                </Button>
              ) : (
                ''
              )}
            </Col>
          </Row>

          <div>
            <Pagination
              data={dataObj}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={telemetryLinkManager.currentPage}>
              <ModelTelemetryLinkListTable
                isOwner={isOwner}
                telemetryLinkManager={telemetryLinkManager}
                IsChangeAllowed={IsChangeAllowed}
                IsRemoveAllowed={IsRemoveAllowed}
                setEditHandler={setEditHandler}
                selectedId={currentEditId}
                disableMode={telemetryLinkManager.showAddEditScreen}
                disableModeHandler={disableModeHandler}
                selectedModelCategoryId={ModelCategoryId}
                ModelId={ModelId}
                onRemoveClick={handleRemove}
              />
            </Pagination>
          </div>
        </div>
      </ModelConfigurationContentTemplate>
    </div>
  );
};

ModelTelemetryLinkList.propTypes = {
  ModelId: PropTypes.string.isRequired,
  userProfileWidgets: PropTypes.object.isRequired
};

export default injectIntl(ModelTelemetryLinkList);
